import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { ToolCallError } from '../../ToolCallError';

interface GetOrgLimitTool {
  name: AgentTools.sf_get_org_limits;
  args: { detailed: boolean };
}

export type GetOrgLimitToolCall = ToolCall<GetOrgLimitTool['name'], GetOrgLimitTool['args']>;

export type GetOrgLimitToolCallSummary = ToolCallSummary<
  GetOrgLimitTool['name'],
  GetOrgLimitTool['args']
>;

interface OrgLimit {
  Max: number;
  Remaining: number;
}

interface OrgLimitsResult {
  DailyApiRequests: OrgLimit;
  DataStorageMB: OrgLimit;
  FileStorageMB: OrgLimit;
  PermissionSets: OrgLimit & { CreateCustom: OrgLimit };
  DailyAsyncApexExecutions: OrgLimit;
  SingleEmail: OrgLimit;
  MassEmail: OrgLimit;
}

export function GetOrgLimit({ toolCall }: { toolCall: GetOrgLimitToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<OrgLimitsResult>(
    toolCall,
    ({ toolCallState }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.sf_get_org_limits.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.sf_get_org_limits.title.errors');
        case ToolCallState.Success:
          return t('toolCalls.sf_get_org_limits.title.success');
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content:
        !toolCall.isRunning && parsedResult ? (
          parsedResult.hasErrors ? (
            <ToolCallError parsedResult={parsedResult} />
          ) : (
            <ToolCallDataList
              items={[
                {
                  label: t('toolCalls.sf_get_org_limits.results.dailyApiRequests'),
                  value: t('toolCalls.sf_get_org_limits.results.limitFormat', {
                    used:
                      parsedResult.results.DailyApiRequests.Max -
                      parsedResult.results.DailyApiRequests.Remaining,
                    max: parsedResult.results.DailyApiRequests.Max,
                    remaining: parsedResult.results.DailyApiRequests.Remaining,
                  }),
                },
                {
                  label: t('toolCalls.sf_get_org_limits.results.dataStorageMB'),
                  value: t('toolCalls.sf_get_org_limits.results.limitFormat', {
                    used:
                      parsedResult.results.DataStorageMB.Max -
                      parsedResult.results.DataStorageMB.Remaining,
                    max: parsedResult.results.DataStorageMB.Max,
                    remaining: parsedResult.results.DataStorageMB.Remaining,
                  }),
                },
                {
                  label: t('toolCalls.sf_get_org_limits.results.fileStorageMB'),
                  value: t('toolCalls.sf_get_org_limits.results.limitFormat', {
                    used:
                      parsedResult.results.FileStorageMB.Max -
                      parsedResult.results.FileStorageMB.Remaining,
                    max: parsedResult.results.FileStorageMB.Max,
                    remaining: parsedResult.results.FileStorageMB.Remaining,
                  }),
                },
                {
                  label: t('toolCalls.sf_get_org_limits.results.permissionSets'),
                  value: t('toolCalls.sf_get_org_limits.results.limitFormat', {
                    used:
                      parsedResult.results.PermissionSets.Max -
                      parsedResult.results.PermissionSets.Remaining,
                    max: parsedResult.results.PermissionSets.Max,
                    remaining: parsedResult.results.PermissionSets.Remaining,
                  }),
                },
                {
                  label: t('toolCalls.sf_get_org_limits.results.dailyAsyncApexExecutions'),
                  value: t('toolCalls.sf_get_org_limits.results.limitFormat', {
                    used:
                      parsedResult.results.DailyAsyncApexExecutions.Max -
                      parsedResult.results.DailyAsyncApexExecutions.Remaining,
                    max: parsedResult.results.DailyAsyncApexExecutions.Max,
                    remaining: parsedResult.results.DailyAsyncApexExecutions.Remaining,
                  }),
                },
                {
                  label: t('toolCalls.sf_get_org_limits.results.singleEmail'),
                  value: t('toolCalls.sf_get_org_limits.results.limitFormat', {
                    used:
                      parsedResult.results.SingleEmail.Max -
                      parsedResult.results.SingleEmail.Remaining,
                    max: parsedResult.results.SingleEmail.Max,
                    remaining: parsedResult.results.SingleEmail.Remaining,
                  }),
                },
                {
                  label: t('toolCalls.sf_get_org_limits.results.massEmail'),
                  value: t('toolCalls.sf_get_org_limits.results.limitFormat', {
                    used:
                      parsedResult.results.MassEmail.Max - parsedResult.results.MassEmail.Remaining,
                    max: parsedResult.results.MassEmail.Max,
                    remaining: parsedResult.results.MassEmail.Remaining,
                  }),
                },
              ]}
            />
          )
        ) : null,
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
